import { pushApi } from './api';

// Convert base64 URL to Uint8Array (needed for VAPID key)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

// Get notification permission status
export const getNotificationPermission = () => {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isPushSupported()) {
    return { success: false, error: 'Push notifications not supported' };
  }
  
  try {
    const permission = await Notification.requestPermission();
    return { success: permission === 'granted', permission };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Subscribe to push notifications
export const subscribeToPush = async () => {
  if (!isPushSupported()) {
    return { success: false, error: 'Push notifications not supported' };
  }
  
  try {
    // Check permission
    if (Notification.permission !== 'granted') {
      const permResult = await requestNotificationPermission();
      if (!permResult.success) {
        return { success: false, error: 'Permission denied' };
      }
    }
    
    // Register service worker
    const registration = await navigator.serviceWorker.register('/sw-push.js');
    await navigator.serviceWorker.ready;
    
    // Get VAPID key
    const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!vapidKey) {
      return { success: false, error: 'VAPID key not configured' };
    }
    
    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey)
    });
    
    // Send subscription to server
    const subscriptionJson = subscription.toJSON();
    await pushApi.subscribe({
      endpoint: subscriptionJson.endpoint,
      keys: subscriptionJson.keys
    });
    
    return { success: true, subscription };
  } catch (error) {
    console.error('Push subscription error:', error);
    return { success: false, error: error.message };
  }
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async () => {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
      await pushApi.unsubscribe();
    }
    
    return { success: true };
  } catch (error) {
    console.error('Push unsubscribe error:', error);
    return { success: false, error: error.message };
  }
};

// Check if currently subscribed to push
export const isSubscribedToPush = async () => {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    return false;
  }
};
